import * as Yup from 'yup';

import { TEXTS } from '../texts';

import type { SlaActionTypeValue } from './formValues';
import { isEmptySelection } from './formValues';

export type GenericTemplateType = 'email' | 'sms';

// Keyed on the generated enum, so a new backend action type is a type error until it is placed here.
const GENERIC_TEMPLATE_TYPE_BY_ACTION: Record<SlaActionTypeValue, GenericTemplateType | null> = {
  notification: null,
  email: 'email',
  sms: 'sms',
  email_suggestion: 'email',
};

/** The generic-template type an action sends, or null for an action that sends no template. */
export const genericTemplateType = (actionType: SlaActionTypeValue | ''): GenericTemplateType | null =>
  actionType === '' ? null : GENERIC_TEMPLATE_TYPE_BY_ACTION[actionType];

const days = () =>
  Yup.number().typeError(TEXTS.REQUIRED).integer(TEXTS.WHOLE_DAYS).min(1, TEXTS.MIN_ONE_DAY).required(TEXTS.REQUIRED);

export const slaRuleValidationSchema = Yup.object({
  display_name: Yup.string().trim().max(255).required(TEXTS.REQUIRED),
  metabase_card_id: Yup.number().typeError(TEXTS.REQUIRED).required(TEXTS.REQUIRED),
  thresholds: Yup.array()
    .of(
      Yup.object({
        sla_days: days(),
        repeat_every_days: days(),
        actions: Yup.array()
          .of(
            Yup.object({
              action_type: Yup.string().required(TEXTS.REQUIRED),
              recipient: Yup.string().required(TEXTS.REQUIRED),
              // The executors fail a fire that has no template to send.
              generic_template_id: Yup.mixed().when('action_type', {
                is: (type: SlaActionTypeValue | '') => genericTemplateType(type) !== null,
                then: (schema) => schema.test('required', TEXTS.REQUIRED, (value) => !isEmptySelection(value)),
              }),
            })
          )
          .min(1),
      })
    )
    .min(1)
    // Mirrors the backend's reject_duplicate_threshold_schedules, so the admin sees it inline.
    .test('distinct-schedules', TEXTS.DUPLICATE_SCHEDULE, (thresholds) => {
      // A threshold still missing its days is unfinished, and its own fields already say so.
      const keys = (thresholds ?? [])
        .filter((t) => Number.isFinite(t.sla_days) && Number.isFinite(t.repeat_every_days))
        .map((t) => `${t.sla_days}/${t.repeat_every_days}`);
      return new Set(keys).size === keys.length;
    }),
});
